import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  SOCKET_URL,
  initializeSocket,
  formatBearerToken,
} from '../config/socket';
import { AuthContext } from './AuthContext';

const SocketContext = createContext(null);

/**
 * هوک داخلی کانتکست سوکت
 */
export const useSocketContext = () => {
  const context = useContext(SocketContext);
  if (!context) {
    return {
      socket: null,
      isConnected: false,
      socketId: null,
      error: null,
      connect: () => {},
      disconnect: () => {},
      emit: () => {},
      on: () => () => {},
      off: () => {},
    };
  }
  return context;
};

/**
 * پرووایدر سراسری مدیریت اتصال Socket.io
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.url] آدرس سرور سوکت (پیش‌فرض: http://localhost:4000)
 * @param {Object} [props.options] گزینه‌های اتصال Socket.io
 * @param {boolean} [props.autoConnect=true] اتصال خودکار به محض لود برنامه
 * @param {string} [props.token] توکن احراز هویت سفارشی
 */
export const SocketProvider = ({
  children,
  url = SOCKET_URL,
  options = {},
  autoConnect = true,
  token,
}) => {
  const authContext = useContext(AuthContext);
  const activeToken = token || authContext?.token || localStorage.getItem('authToken');

  const [isConnected, setIsConnected] = useState(false);
  const [socketId, setSocketId] = useState(null);
  const [error, setError] = useState(null);

  // نگهداری سوکت در ref برای پایداری در تمام رندرها
  const socketRef = useRef(null);
  if (!socketRef.current) {
    const bearerToken = formatBearerToken(activeToken);
    const { extraHeaders: customHeaders, auth: customAuth, ...restOptions } = options;
    socketRef.current = initializeSocket(url, {
      ...restOptions,
      extraHeaders: {
        ...(customHeaders || {}),
        ...(bearerToken ? { authorization: bearerToken } : {}),
      },
      auth: {
        ...(customAuth || {}),
        ...(bearerToken ? { authorization: bearerToken, token: bearerToken } : {}),
      },
    });
  }
  const socket = socketRef.current;

  // همگام‌سازی هدر authorization و auth با سوکت
  const prevTokenRef = useRef(activeToken);
  useEffect(() => {
    if (!socket) return;

    const bearerToken = formatBearerToken(activeToken);

    if (bearerToken) {
      if (socket.io && socket.io.opts) {
        socket.io.opts.extraHeaders = {
          ...(socket.io.opts.extraHeaders || {}),
          authorization: bearerToken,
        };
      }
      socket.auth = {
        ...(socket.auth || {}),
        token: bearerToken,
        authorization: bearerToken,
      };
    } else {
      if (socket.io?.opts?.extraHeaders) {
        delete socket.io.opts.extraHeaders.authorization;
      }
      if (socket.auth) {
        delete socket.auth.token;
        delete socket.auth.authorization;
      }
    }

    // اگر توکن پس از لاگین/لاگ‌اوت تغییر کرد و سوکت متصل بود، برای اعمال احراز هویت مجدد وصل می‌شویم
    if (prevTokenRef.current !== activeToken && socket.connected) {
      socket.disconnect().connect();
    }
    prevTokenRef.current = activeToken;
  }, [socket, activeToken]);

  // مدیریت رویدادهای اتصال سوکت
  useEffect(() => {
    if (!socket) return;

    const handleConnect = () => {
      setIsConnected(true);
      setSocketId(socket.id);
      setError(null);
    };

    const handleDisconnect = (reason) => {
      setIsConnected(false);
      setSocketId(null);
      if (reason === 'io server disconnect') {
        // قطع ارتباط از سمت سرور؛ تلاش مجدد دستی
        socket.connect();
      }
    };

    const handleConnectError = (err) => {
      setIsConnected(false);
      setError(err);
      console.warn('⚠️ [Socket.io] Connection error:', err.message || err);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);

    // بررسی وضعیت فعلی
    if (socket.connected) {
      setIsConnected(true);
      setSocketId(socket.id);
    }

    // اتصال در صورت فعال بودن autoConnect
    if (autoConnect && !socket.connected) {
      socket.connect();
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
    };
  }, [socket, autoConnect]);

  // توابع کمکی
  const connect = useCallback(() => {
    if (socket && !socket.connected) {
      socket.connect();
    }
  }, [socket]);

  const disconnect = useCallback(() => {
    if (socket && socket.connected) {
      socket.disconnect();
    }
  }, [socket]);

  const emit = useCallback(
    (eventName, ...args) => {
      if (socket) {
        socket.emit(eventName, ...args);
      }
    },
    [socket]
  );

  const on = useCallback(
    (eventName, callback) => {
      if (!socket) return () => {};
      socket.on(eventName, callback);
      return () => {
        socket.off(eventName, callback);
      };
    },
    [socket]
  );

  const off = useCallback(
    (eventName, callback) => {
      if (socket) {
        socket.off(eventName, callback);
      }
    },
    [socket]
  );

  const contextValue = useMemo(
    () => ({
      socket,
      isConnected,
      socketId,
      error,
      connect,
      disconnect,
      emit,
      on,
      off,
    }),
    [socket, isConnected, socketId, error, connect, disconnect, emit, on, off]
  );

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
